/**
 * Stepping Stones — Networking hub. The 5 tab panels live in
 * /app/frontend/src/components/network/ so this file stays slim.
 *
 * Routes:
 *   /network?t=pitch     → Elevator Pitch Studio
 *   /network?t=people    → Stepping Stones (Grid + Career Path views)
 *   /network?t=sparks    → Conversation Sparks
 *   /network?t=mindset   → Mindset Coach
 *   /network?t=events    → Events & Communities
 */
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import {
  Footprints, Mic2, Users, Sparkles, Brain, MapPin, HelpCircle,
} from "lucide-react";

import PitchPanel   from "../components/network/PitchPanel";
import PeoplePanel  from "../components/network/PeoplePanel";
import SparksPanel  from "../components/network/SparksPanel";
import MindsetPanel from "../components/network/MindsetPanel";
import EventsPanel  from "../components/network/EventsPanel";
import NetworkTour  from "../components/network/NetworkTour";

const TABS = [
  { id: "pitch",     label: "Elevator Pitch",      icon: Mic2,     eyebrow: "Land in the room" },
  { id: "people",    label: "Stepping Stones",     icon: Users,    eyebrow: "Your contact graph" },
  { id: "sparks",    label: "Conversation Sparks", icon: Sparkles, eyebrow: "Openers + reciprocity" },
  { id: "mindset",   label: "Mindset Coach",       icon: Brain,    eyebrow: "Walk in grounded" },
  { id: "events",    label: "Events & Communities", icon: MapPin,  eyebrow: "Where to show up" },
];

export default function Network() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("t") || "pitch";
  const setTab = (t) => setParams({ t });
  const [tourOpen, setTourOpen] = useState(false);

  return (
    <div className="min-h-screen rm-paper pb-20">
      <Navbar />
      <NetworkTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="rm-tag mb-3" data-testid="network-eyebrow">
              <Footprints size={12} /> Stepping Stones
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none">
              Build your <span className="rm-text-orange">network.</span>
            </h1>
            <p className="text-base md:text-lg mt-3 max-w-2xl" style={{ color: "#475569" }}>
              Your career path is paved with people. Each contact is a stepping stone —
              and Resmo helps you craft the words, mindset, and asks to walk it.
            </p>
            <button
              onClick={() => setTourOpen(true)}
              className="rm-btn rm-btn-ghost text-xs px-3 py-1.5 mt-3"
              data-testid="network-tour-open"
              title="Re-take the guided tour"
            >
              <HelpCircle size={12} /> Take the tour
            </button>
          </div>
          <ResmoMascot state="satisfied" size={130} />
        </div>

        {/* Tab strip */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="network-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className="rm-btn text-xs px-3 py-2"
                style={{
                  background: active ? "linear-gradient(135deg,#FF6A00,#B45309)" : "white",
                  color: active ? "white" : "#0F1B2D",
                  border: "3px solid #0F1B2D",
                  boxShadow: active ? "2px 2px 0 0 #0F1B2D" : "none",
                  fontWeight: 700,
                }}
                data-testid={`network-tab-${t.id}`}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "pitch"   && <PitchPanel />}
        {tab === "people"  && <PeoplePanel onJumpTo={(t) => setTab(t)} />}
        {tab === "sparks"  && <SparksPanel />}
        {tab === "mindset" && <MindsetPanel />}
        {tab === "events"  && <EventsPanel />}
      </div>
    </div>
  );
}
